import React, { useEffect, useState } from 'react';
import { API, authFetch } from './api';

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

const STATUS_STYLE = {
  paid: 'bg-[#00E676]/15 text-[#00E676]',
  unpaid: 'bg-amber-500/15 text-amber-300',
};

// Invoices & billing runs for an association admin (Section 8).
// Line items bill AUTHORIZED seat caps per enabled product — generated server-side.
export default function AssociationBilling({ assoc }) {
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [running, setRunning] = useState(false);
  const [view, setView] = useState(null); // full invoice for the print modal

  const load = () => authFetch(`${API}/api/associations/${assoc.id}/invoices`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load invoices'))))
    .then(setInvoices).catch((e) => setError(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assoc.id]);

  const runBilling = async () => {
    setRunning(true); setMsg(''); setError('');
    try {
      const res = await authFetch(`${API}/api/associations/${assoc.id}/billing-runs`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Billing run failed');
      setMsg(data.created > 0
        ? `Generated ${data.created} invoice${data.created === 1 ? '' : 's'} for ${data.period}.`
        : `No new invoices — all billable brokerages are already invoiced for ${data.period}.`);
      load();
    } catch (e) { setError(e.message); } finally { setRunning(false); }
  };

  const markPaid = async (inv) => {
    const ref = window.prompt(`Mark invoice ${inv.invoice_number} paid.\nOptional payment reference (check #, wire, etc.):`, '');
    if (ref === null) return; // cancelled
    setError(''); setMsg('');
    try {
      const res = await authFetch(`${API}/api/associations/${assoc.id}/invoices/${inv.id}/mark-paid`,
        { method: 'POST', body: JSON.stringify({ reference: ref || null }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to mark paid');
      load();
    } catch (e) { setError(e.message); }
  };

  const openInvoice = async (inv) => {
    setError('');
    try {
      const res = await authFetch(`${API}/api/associations/${assoc.id}/invoices/${inv.id}`);
      if (!res.ok) throw new Error('Failed to load invoice');
      setView(await res.json());
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="text-white font-semibold">Invoices &amp; Billing</h2>
        <button onClick={runBilling} disabled={running}
          className="px-4 py-2 rounded-md bg-[#00D4FF] text-[#0A0E27] font-semibold text-sm disabled:opacity-50">
          {running ? 'Running…' : 'Run billing for this month'}
        </button>
      </div>
      <p className="text-xs text-[#B0BEC5] mb-3">
        Generates one invoice per billable (post-trial, active) brokerage, billing its authorized
        seat caps per enabled product × your per-seat price. Running again in the same month is safe —
        already-invoiced brokerages are skipped.
      </p>
      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
      {msg && <p className="text-[#00E676] text-sm mb-2">{msg}</p>}

      {invoices == null ? (
        <p className="text-[#B0BEC5] text-sm">Loading…</p>
      ) : invoices.length === 0 ? (
        <p className="text-[#B0BEC5] text-sm">No invoices yet. Run billing to generate the first batch.</p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div key={inv.id}
              className="rounded-lg border border-[#546E7A]/25 bg-[#0A0E27]/40 p-3 flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium break-words">{inv.brokerage_name}</span>
                  <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${STATUS_STYLE[inv.status] || 'text-[#B0BEC5]'}`}>
                    {inv.status}
                  </span>
                </div>
                <div className="text-xs text-[#B0BEC5] break-words">
                  {inv.invoice_number} · {inv.period} · due {inv.due_date}
                  {inv.paid_date ? ` · paid ${inv.paid_date}` : ''}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-white font-semibold">{money(inv.total)}</span>
                <button onClick={() => openInvoice(inv)}
                  className="px-3 py-1.5 rounded-md border border-[#546E7A]/40 text-[#B0BEC5] text-sm hover:text-white">
                  View
                </button>
                {inv.status !== 'paid' && (
                  <button onClick={() => markPaid(inv)}
                    className="px-3 py-1.5 rounded-md bg-[#00E676]/15 text-[#00E676] text-sm hover:bg-[#00E676]/25">
                    Mark paid
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {view && <InvoiceModal inv={view} assoc={assoc} onClose={() => setView(null)} />}
    </div>
  );
}

function InvoiceModal({ inv, assoc, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 overflow-y-auto p-3 sm:p-6" onClick={onClose}>
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #invoice-sheet, #invoice-sheet * { visibility: visible !important; }
        #invoice-sheet { position: absolute !important; inset: 0 !important; box-shadow: none !important; }
        .no-print { display: none !important; }
      }`}</style>
      <div className="max-w-2xl w-full mx-auto my-2" onClick={(e) => e.stopPropagation()}>
        <div id="invoice-sheet" className="bg-white text-gray-900 rounded-lg p-5 sm:p-8">
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 pb-4 mb-4">
            <div className="min-w-0">
              <div className="text-lg font-bold break-words">{inv.association_display_name || assoc.display_name}</div>
              <div className="text-xs text-gray-500">Invoice</div>
            </div>
            <div className="text-right text-sm shrink-0">
              <div className="font-semibold">{inv.invoice_number}</div>
              <div className="text-gray-500">Period {inv.period}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Bill to</div>
              <div className="font-medium break-words">{inv.bill_to_name || inv.brokerage_name}</div>
              {inv.bill_to_email && <div className="text-gray-600 break-words">{inv.bill_to_email}</div>}
              {inv.bill_to_address && <div className="text-gray-600 whitespace-pre-wrap break-words">{inv.bill_to_address}</div>}
            </div>
            <div className="sm:text-right">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Details</div>
              <div>Invoice date: {inv.invoice_date}</div>
              <div>Due date: {inv.due_date} (Net {inv.terms_days})</div>
              <div>Status: <span className="font-semibold">{inv.status}</span>{inv.paid_date ? ` · ${inv.paid_date}` : ''}</div>
            </div>
          </div>

          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-b border-gray-300 text-left text-gray-500">
                <th className="py-1.5 font-medium">Description</th>
                <th className="py-1.5 font-medium text-right">Seats</th>
                <th className="py-1.5 font-medium text-right">Unit</th>
                <th className="py-1.5 font-medium text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(inv.line_items || []).map((l, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1.5 break-words">{l.description}</td>
                  <td className="py-1.5 text-right">{l.seats}</td>
                  <td className="py-1.5 text-right">{money(l.unit_price)}</td>
                  <td className="py-1.5 text-right">{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="py-2" colSpan={3}>Total due</td>
                <td className="py-2 text-right">{money(inv.total)}</td>
              </tr>
            </tfoot>
          </table>

          {(inv.remit_to_name || inv.remit_to_address || inv.remit_to_instructions) && (
            <div className="text-xs text-gray-600 border-t border-gray-200 pt-3">
              <div className="uppercase tracking-wide text-gray-400 mb-1">Remit to</div>
              {inv.remit_to_name && <div className="font-medium">{inv.remit_to_name}</div>}
              {inv.remit_to_address && <div className="whitespace-pre-wrap break-words">{inv.remit_to_address}</div>}
              {inv.remit_to_instructions && <div className="whitespace-pre-wrap break-words mt-1">{inv.remit_to_instructions}</div>}
            </div>
          )}
        </div>

        <div className="no-print flex justify-end gap-2 mt-3">
          <button onClick={onClose}
            className="px-4 py-2 rounded-md border border-[#546E7A]/40 text-[#B0BEC5] text-sm hover:text-white">
            Close
          </button>
          <button onClick={() => window.print()}
            className="px-4 py-2 rounded-md bg-[#00D4FF] text-[#0A0E27] font-semibold text-sm">
            Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  );
}
